import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './CaseStudies.css';

const CaseStudies = () => {
  const [selectedFilter, setSelectedFilter] = useState('All');

  const caseStudies = [
    {
      id: 1,
      title: 'Streamlining Operations Through Intelligent Automation',
      client: "Mary Macleod's Shortbread",
      industry: 'Small/Medium Sized Business',
      service: 'Integrations & Automation',
      logo: '/logo-mms.jpg',
      challenge:
        "The client was struggling to plan production effectively because the data they needed lived inside their ERP and was difficult to access in a usable form. Exporting information was slow, manual, and inefficient, and even when data was extracted, it still required significant transformation before it could support real decision-making. At the same time, incoming customer orders from a large Canadian retailer were arriving through EDI providers and had to be manually handled before entering the ERP, adding delays and risk. The lack of live, connected data across systems made planning harder than it needed to be and limited the company’s ability to move quickly and accurately.",
      process:
        "To address this challenge, we focused on a practical, flexible approach that delivered results without unnecessary complexity or cost. We designed B.A.K.E.R. as a modular web application using a proven, well-documented technology stack, making it easy to extend, maintain, and adapt as business needs evolved. By building directly around existing workflows, we ensured the solution could pull, transform, and deliver data automatically without disrupting operations. This approach kept development efficient, reduced long-term maintenance risk, and created a foundation that could support additional integrations and custom processes well beyond the original use case.",
      solution:
        'Today, B.A.K.E.R. has evolved into a powerful integration and automation platform that sits at the center of the client’s operations. It manages data from multiple sources and seamlessly connects systems, such as linking sales CRM data with marketing platforms to create a unified view of the business. B.A.K.E.R. automates order fulfillment across several large customers, ensuring order data flows accurately into the ERP, spreadsheets, and other critical tools without manual intervention. It also tracks daily inventory levels across multiple customers and continuously validates data between systems, proactively flagging issues before they impact operations. What started as a focused solution has become a reliable engine for efficiency, visibility, and control.',
      testimonial: {
        quote:
          'Partnering with Snap Remedy over the past five+ years has transformed the way we operate. They’ve played a critical role in modernizing our business by building powerful system integrations that connect our internal platforms, like our ERP, with external systems—eliminating manual data entry and significantly reducing errors. Their ability to streamline and automate key processes, especially within our production facility, has led to meaningful efficiency gains and allowed our team to focus on higher-value work. Beyond automation, Snap Remedy has supported nearly every aspect of our technology ecosystem. From developing and maintaining our websites to providing responsive technical support for our entire team, they’ve been a consistent and reliable partner. They also manage our cybersecurity and data backup strategies, giving us confidence that our operations are secure and resilient. It’s rare to find a partner with such a broad skill set and commitment to delivering results—Snap Remedy has truly become an extension of our business.',
        author: 'Sharon Macleod',
        role: "Mary Macleod's Shortbread",
        date: 'October 1, 2025'
      },
      results: [
        'Faster and more accurate production planning with live, reliable data',
        'Manual data handling was reduced significantly, saving time and lowering error rates',
        'Operations scaled smoothly as new customers, data sources, and workflows were added'
      ],
      thumbnail: 'healthcare',
      featured: true
    },
    {
      id: 2,
      title: 'Next level recreational soccer website',
      client: 'Multiple recreational soccer organizations',
      industry: 'Clinics',
      service: 'Website Design',
      logo: '',
      challenge:
        'The challenge was to break away from the long-standing pattern of recreational soccer websites that are clunky, confusing, and visually dated. Most existing sites—even for large organizations—rely on third-party platforms to handle registrations, divisions, schedules, and scoring, resulting in two mismatched systems layered on top of each other. This creates poor navigation, inconsistent design, and a fragmented user experience for players, parents, coaches, and managers. The functionality is often generic, bloated with unused features, and difficult to navigate, making even simple tasks frustrating. Despite how central these websites are to league operations, most have remained largely unchanged for decades, failing to reflect how people expect modern, easy-to-use digital experiences to work.',
      process:
        "Our approach was to rethink the experience from the ground up by building a single, unified platform that handled both the front end and back end seamlessly. We focused on combining ease of use with strong, modern design so the site felt intuitive, engaging, and enjoyable to use for everyone involved. Beyond core league functionality, we placed a strong emphasis on preserving and showcasing league, team, and player history—creating dedicated spaces that highlight past seasons, achievements, and standout performances. Rich content and social media connectivity were woven throughout the experience, with photo galleries, videos, historical highlights, and user engagement features working together to keep the site active and compelling. The goal was to create a platform that players and managers actually want to use, while honoring the decades of history that make recreational leagues meaningful to their communities.",
      solution:
        'The solution is a modern, unified league platform designed to feel professional while remaining approachable for recreational players and managers. The first version of this vision is already live with the Toronto Premier League, delivering a clean, intuitive experience that brings league operations, history, and content together in one cohesive system. As the platform evolves toward its second major phase in 2026, it will introduce advanced features such as automated scoring after matches, automatic social media updates, and full-game and highlight video integration. Built to grow alongside the league, the site is designed to continuously adapt and improve—keeping users engaged, celebrating league history, and bringing a polished, professional feel to recreational soccer without losing its community-first spirit.',
      results: [
        'League, team, and player histories were preserved in a living archive, creating long-term value and a deeper emotional connection for the community',
        'By removing unused, generic functionality, the site became faster, clearer, and far more engaging—encouraging players and managers to actively explore stats and content',
        'The platform established a foundation for continuous growth, allowing new features to be added over time instead of becoming stagnant and outdated'
      ],
      thumbnail: 'finance',
      featured: false
    },
    {
      id: 3,
      title: 'Email and Workstation security',
      client: 'Aeras Water',
      industry: 'Home Professionals',
      service: 'Security monitoring',
      logo: '',
      challenge:
        'Aeras Water was in a period of rapid growth, and with that growth came the need to standardize and strengthen its IT environment. As headcount increased and more devices entered the ecosystem, secure access to sensitive data became both more critical and more complex. Teams needed to collaborate seamlessly across onsite and offsite locations without slowing down operations or introducing risk. The challenge was finding a security approach that could scale with the business, protect valuable data, and still allow people to work efficiently—without overcomplicating day-to-day workflows.',
      process:
        "Our approach was to implement a security solution that delivered maximum protection without adding unnecessary complexity. Our solution has a small footprint that could integrate cleanly into Aeras Water’s existing workflows, ensuring security enhancements didn’t interrupt critical operations or slow teams down. The solution was built to be proactive rather than reactive, continuously monitoring the environment for threats while automatically handling issues like malware, suspicious spam, and other malicious activity in the background. The result is an always-on layer of protection that users can trust—quietly securing data, detecting risks early, and allowing the team to stay focused on their work with confidence.",
      solution:
        'The solution centers on deploying a best-in-class, cloud-based security platform from our trusted digital security partner—the #1 globally-ranked provider for business protection. By rolling out these advanced endpoints across Aeras Water’s environment, we gained real-time visibility and the ability to detect and respond to threats as they happen. Most malicious activity is automatically identified, quarantined, and contained before it can cause any damage, while continuous scanning feeds live data back to our team. This gives us the proactive “early warning system” needed to stay ahead of issues and keep systems secure, allowing our clients to operate with confidence knowing their data and devices are protected around the clock.',
      results: [
        'Enterprise-grade security that scaled with growth, protecting more users and devices without adding operational complexity.',
        'Potential threats are now detected and neutralized in real time, dramatically reducing risk and downtime across the organization',
        'Teams can collaborate and share sensitive data confidently, knowing their systems are continuously monitored and protected in the background'
      ],
      thumbnail: 'retail',
      featured: false
    },
    {
      id: 4,
      title: 'Knowledgebase of digitized building documentation',
      client: "Federation of Metro Tenants' Association",
      industry: 'NGOs or Unions',
      service: 'Integration and automation',
      logo: '',
      challenge:
        "The Federation of Metro Tenants Association faced a growing challenge with decades of paper-based records that were slow to search, difficult to organize, and physically demanding to maintain. Staff relied on these documents frequently, yet finding critical information often meant digging through filing cabinets and boxes, adding unnecessary friction to already time-sensitive work. The system became increasingly stressful, space-consuming, and prone to human error, limiting the organization's ability to work efficiently and focus on supporting tenants.",
      process:
        "Our process focused on creating a solution that was easy to maintain, accessible, and efficient for daily use. We digitized the existing paper records and transcribed the information into a cloud-based, searchable database, making it simple for staff to quickly locate key details. Speed of data retrieval and intuitive access were central to the design, ensuring that employees could work confidently and efficiently without the stress and delays of a manual filing system. This approach laid the foundation for a streamlined, modern records management system that supports the organization's ongoing work.",
      solution:
        "We developed a secure, cloud-based web application with a clean, intuitive interface that allows staff to search using a variety of criteria quickly and efficiently. By digitizing and centralizing the records, we significantly reduced manual tasks, increasing the organization's capacity to handle client inquiries. Staff can now access the information they need in moments, enabling faster, more effective service without the frustration of sifting through paper files. The system is easy to maintain, and with secure cloud storage, there's peace of mind knowing that critical data is protected against loss or disaster.",
      results: [
        'Staff can locate and access critical records instantly, improving response times.',
        'Manual filing tasks were greatly reduced, increasing overall operational efficiency.',
        'Secure cloud storage protects records and eliminates risk of loss or damage.'
      ],
      thumbnail: 'manufacturing',
      featured: false
    },
    {
      id: 5,
      title: "Technical Support that's a tier above",
      client: 'Universal Staffing',
      industry: 'Multiple Business Locations',
      service: 'Technical Support',
      logo: '',
      challenge:
        "Universal Staffing faced the challenge of managing technology across multiple locations in several cities, supporting a large number of workstations, networking, printers, phones, software, and other critical office systems. In a fast-paced environment, issues had to be addressed immediately, making reliability and trust in an IT partner essential. The company needed proven solutions that could keep operations running smoothly while supporting many users simultaneously. On top of day-to-day IT support, having a partner who could also manage and host their website and secure their infrastructure was highly desirable, creating a single source for comprehensive technology management.",
      process:
        "Our process leverages extensive experience across network administration, desktop support, and data management to deliver fast, reliable service. All of our technicians are fully equipped to resolve issues on the first call whenever possible, minimizing downtime and allowing clients to focus on their work. Speed of resolution and quality of service are central to our approach, because we understand that critical systems demand near-constant uptime. By combining expertise with proactive support, we ensure that when problems arise, our clients can trust us to handle them efficiently and effectively.",
      solution:
        "Our solution provides Universal Staffing with immediate access to a team of skilled technicians who can quickly assess and resolve most IT issues on the first contact. With Snap Remedy as their partner, technical support is always just a click away, eliminating the need for staff to spend valuable time troubleshooting problems themselves. This ensures critical systems remain online and available at all times, giving the company confidence that operations will continue smoothly. Whether it’s day-to-day issues or unexpected emergencies, Universal Staffing relies on us to respond quickly and keep their technology running without interruption.",
      results: [
        'Most IT issues are resolved on the first contact, minimizing downtime',
        'Staff save valuable time by relying on instant, expert support',
        'Critical systems stay online, ensuring reliable operations across all locations'
      ],
      thumbnail: 'education',
      featured: false
    },
    {
      id: 6,
      title: 'Designing a Logo That Honors Craftsmanship',
      client: 'The 5 & Dime Upcycled Furniture, Antiques & Accents',
      industry: 'Retail Stores',
      service: 'Brand Identity',
      logo: '',
      challenge:
        "The challenge was creating a logo that captured two seemingly opposing qualities at once. The name “The 5 and Dime” naturally evokes an old-school, vintage feel tied to antiques and history, while the company's work brings those pieces forward with modern artistry and design. The logo needed to respect the character and nostalgia of restored furniture while clearly signaling that this was not traditional restoration, but thoughtful upcycling with a contemporary edge. Balancing classic charm with modern expression—while aligning with the tagline upcycled furniture, antiques and accents—was essential to accurately reflect the brand's identity and creative vision.",
      process:
        "Our process focused on collaboration and careful exploration to meet the client’s high standard for artistic quality. Through detailed discussions, we explored a wide range of visual directions, typography styles, and design elements to understand what best represented their vision. Each option was thoughtfully considered, weighing how well it balanced vintage character with modern refinement. By working closely with the client at every step, we were able to narrow in on a direction that felt authentic to their craft and aligned with the level of care and creativity they bring to every piece they restore.",
      solution:
        "The final solution was a logo that the client felt fully confident standing behind. The design reflects the look and feel of an early-1900s storefront sign, capturing the nostalgic character of the “5 and Dime” name while remaining clean and intentional in its execution. This timeless, old-style aesthetic aligns perfectly with their focus on upcycled furniture, antiques, and artistic accents. The client was thrilled with the result and now uses the logo prominently across social media, apparel, and branding materials, making it a central and recognizable part of their identity.",
      results: [
        'The brand now has a logo that authentically reflects its artistic craftsmanship',
        'Strong visual recognition across social media and apparel increased brand presence',
        'A timeless design supports long-term brand consistency and growth'
      ],
      thumbnail: 'technology',
      featured: false
    },
    {
      id: 8,
      title: 'Reliable Hosting and Technical Support for a Growing Media Team',
      client: 'Pop Sandbox',
      industry: 'Small/Medium Sized Business',
      service: 'Hosting & Technical Support',
      logo: 'logo-ps.jpg',
      challenge:
        'Pop Sandbox needed a dependable technical partner to take over hosting, stabilize websites with longstanding issues, and support ongoing growth for both internal projects and shared client work.',
      process:
        'Our approach focused on stabilizing infrastructure first, then building a stronger long-term partnership around responsiveness, communication, and collaborative technical delivery. We prioritized a smooth hosting transition, resolved persistent platform issues, and created a support relationship that could flex between daily technical needs and larger shared client initiatives.',
      solution:
        'Snap Remedy stepped in to assume hosting responsibilities, resolve persistent website issues, and provide responsive day-to-day support. The partnership also expanded into collaborative project delivery, where technical execution and clear communication helped move shared client work forward efficiently.',
      testimonial: {
        quote:
          'Working with Snap Remedy over the past seven-plus years has been a game-changer for our team. As a media company, reliable hosting and responsive technical support are critical to everything we do, and Snap Remedy has consistently delivered on both fronts. They stepped in seamlessly to take over our hosting environment, quickly resolving longstanding website issues with some of our projects that had been holding us back and ensuring our platforms run smoothly and securely. Beyond day-to-day support, Snap Remedy has proven to be a true partner in growth. Their collaborative approach was especially evident when we worked together on developing a new website for a shared client—bringing technical expertise, clear communication, and a solutions-focused mindset every step of the way. It’s rare to find an IT partner that combines reliability with strategic thinking, but Snap Remedy does exactly that. They’ve become an essential extension of our team, and we confidently rely on them for both ongoing support and future initiatives.',
        author: 'Alex Jansen',
        role: 'Pop Sandbox',
        date: 'January 20, 2026'
      },
      results: [
        'Hosting transitioned smoothly with ongoing stability and security',
        'Longstanding website issues were resolved quickly',
        'A reliable technical partner now supports both current and future initiatives'
      ],
      thumbnail: 'technology',
      featured: false
    },
    {
      id: 9,
      title: 'Collaborative Website Development Built Around Client Goals',
      client: 'Full Voice Music',
      industry: 'E-commerce',
      service: 'Website Design',
      logo: 'logo-fvm.png',
      challenge: 'Full Voice Music needed a website partner who could listen carefully, collaborate closely, and deliver modern strategy without the frustration they had experienced with previous developers.',
      process: 'We centered the work on communication, clarity, and shared decision-making. By listening closely to goals, keeping timelines visible, and bringing current design and technical thinking to the table, we created a process that felt collaborative rather than transactional.',
      solution: 'Snap Remedy provided a collaborative design and development process built around listening, clear timelines, and responsive problem-solving. The result was a more supportive experience and a modern website strategy aligned with the client’s goals.',
      testimonial: {
        quote: 'After working with several other website developers, we are thrilled (relieved!) to partner with Jason and the team at SnapRemedy. Jason truly listens to our goals, meets timelines, and makes us feel genuinely included in the design process while offering innovative, up-to-date strategies for building an effective website. The SnapRemedy team is professional, responsive, and incredibly efficient at problem-solving. We highly recommend SnapRemedy to any business looking for a reliable, collaborative, and thoughtful website partner.',
        author: 'Nikki Loney',
        role: 'Full Voice Music',
        date: 'July 14, 2025'
      },
      results: [
        'A more collaborative and transparent website process',
        'Timelines were met with responsive communication',
        'The client gained a thoughtful long-term website partner'
      ],
      thumbnail: 'finance',
      featured: false
    },
    {
      id: 7,
      title: 'Your Success Story Starts Here',
      client: 'Your Business',
      industry: 'All',
      challenge:
        "Every successful business starts with a vision. You've seen how we've helped companies transform their operations. Now it's your turn to join the ranks of businesses that have achieved measurable results through strategic IT solutions.",
      solution:
        "Whether you're facing infrastructure challenges, compliance requirements, or scalability issues, Snap Remedy has the expertise to help. We'll work closely with you to understand your unique needs and deliver customized solutions that drive real business value. Your transformation begins with a single conversation.",
      results: [
        'Custom solutions for your business',
        'Proven track record of success',
        'Your results could be next'
      ],
      thumbnail: 'technology',
      featured: false,
      isCTA: true
    }
  ];

  const filters = [
    'All',
    'Small/Medium Sized Business',
    'Clinics',
    'Retail Stores',
    'Home Professionals',
    'NGOs or Unions',
    'Multiple Business Locations',
    'E-commerce'
  ];

  const ctaCard = caseStudies.find((cs) => cs.isCTA);
  const regularCaseStudies = caseStudies.filter((cs) => !cs.isCTA);

  const filteredCaseStudies =
    selectedFilter === 'All'
      ? regularCaseStudies
      : regularCaseStudies.filter((cs) => cs.industry === selectedFilter);

  return (
    <div className="case-studies">
      <section className="hero">
        <div className="hero-container">
          <motion.div className="case-studies-hero-content">
            <div className="hero-label">
              <span className="label-dot"></span>
              Success Stories
            </div>

            <h1 className="hero-title">
              Real Results,
              <span className="highlight">
                Real Impact &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
              </span>
            </h1>

            <p className="hero-description">
              See how Snap Remedy has helped businesses across industries transform their IT infrastructure, reduce costs, and improve day-to-day operations. From small teams to multi-location enterprises, our proven solutions deliver measurable results. We focus on long-term efficiency, stronger security, and scalable systems that grow with your business and support lasting success.
            </p>
          </motion.div>

          <div>
            <img
              src="hero-snap-remedy-casestudies.svg"
              className="hero-img"
              alt="Illustration of people and a big lightbulb"
            />
          </div>
        </div>
      </section>

      <section className="filter-section">
        <div className="container">
          <div className="filter-bar">
            {filters.map((filter) => (
              <button
                key={filter}
                className={`filter-button ${selectedFilter === filter ? 'active' : ''}`}
                onClick={() => setSelectedFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="case-studies-grid-section section-spacing">
        <div className="container">
          <div className="case-studies-grid">
            {filteredCaseStudies.map((caseStudy) => (
              <motion.div
                key={caseStudy.id}
                id={`case-study-${caseStudy.id}`}
                className={`case-study-card ${caseStudy.featured ? 'featured' : ''}`}
              >
                <div className="case-study-content">
                  {caseStudy.featured && <span className="featured-badge">Featured</span>}

                  <div className="case-study-meta">
                    <div className="case-study-logo-wrap">
                      {caseStudy.logo ? (
                        <img
                          src={caseStudy.logo}
                          alt={`${caseStudy.client} logo`}
                          className="case-study-logo"
                        />
                      ) : (
                        <span className="case-study-client fallback-client-name">
                          {caseStudy.client}
                        </span>
                      )}
                    </div>

                    <span className="case-study-industry">{caseStudy.industry}</span>
                  </div>

                  <h2 className="case-study-title">{caseStudy.title}</h2>

                  <div className="case-study-section">
                    <h3 className="section-label">Challenge</h3>
                    <p className="section-text">{caseStudy.challenge}</p>
                  </div>

                  {caseStudy.process && (
                    <div className="case-study-section">
                      <h3 className="section-label">Process</h3>
                      <p className="section-text">{caseStudy.process}</p>
                    </div>
                  )}

                  <div className="case-study-section">
                    <h3 className="section-label">Solution</h3>
                    <p className="section-text">{caseStudy.solution}</p>
                  </div>

                  {caseStudy.testimonial && (
                    <div className="case-study-testimonial">
                      <h3 className="section-label">Testimonial</h3>
                      <blockquote className="testimonial-full-quote">
                        {caseStudy.testimonial.quote}
                      </blockquote>
                      <div className="testimonial-full-meta">
                        <strong>{caseStudy.testimonial.author}</strong>
                        <span>{caseStudy.testimonial.role}</span>
                        <span>{caseStudy.testimonial.date}</span>
                      </div>
                    </div>
                  )}

                  <div className="case-study-results">
                    <h3 className="section-label">Results</h3>
                    <div className="results-grid">
                      {caseStudy.results.map((result, i) => (
                        <div key={i} className="result-item">
                          {result}
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link to="/contact" className="case-study-cta">
                    Learn More →
                  </Link>
                </div>
              </motion.div>
            ))}

            {ctaCard && (
              <motion.div
                key={ctaCard.id}
                id={`case-study-${ctaCard.id}`}
                className="case-study-card cta-card"
              >
                <div className="case-study-content">
                  <div className="case-study-meta">
                    <span className="case-study-industry">
                      {selectedFilter === 'All' ? 'All Industries' : selectedFilter}
                    </span>
                  </div>

                  <h2 className="case-study-title">{ctaCard.title}</h2>

                  <div className="case-study-section">
                    <h3 className="section-label">Your Opportunity</h3>
                    <p className="section-text">{ctaCard.challenge}</p>
                  </div>

                  <div className="case-study-section">
                    <h3 className="section-label">How We Can Help</h3>
                    <p className="section-text">{ctaCard.solution}</p>
                  </div>

                  <div className="case-study-results">
                    <h3 className="section-label">What You Can Achieve</h3>
                    <div className="results-grid">
                      {ctaCard.results.map((result, i) => (
                        <div key={i} className="result-item">
                          {result}
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link to="/contact" className="case-study-cta cta-primary">
                    Start Your Success Story →
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <section className="case-studies-cta section-spacing">
        <div className="container">
          <motion.div className="cta-box">
            <h2 className="cta-title">Ready to Create Your Success Story?</h2>
            <p className="cta-description">
              Join the hundreds of businesses that have transformed their operations with Snap Remedy. Whether you're a small business, multi-location enterprise, or non-profit organization, we have the expertise to deliver IT solutions that drive real results. Let's discuss how we can help you achieve your technology goals.
            </p>
            <Link to="/contact#contact-form" className="btn-cta-primary">
              Start Your Project
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;